import React, { useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import styled from "styled-components";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import SupplierContext from "@contexts/SupplierContext";
import UserContext from "@contexts/UserContext";
import UiContext from "@contexts/UiContext";
import { SocketContext } from "@contexts/SocketContext";
import EndpointsEnum from "@models/EndpointsEnum";
import INotification from "@models/INotification";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import ISupplier_Integration from "@models/ISupplier_Integration";
import IUpdateSupplierIntegrationsDTO from "@models/DTO/IUpdateSupplierIntegrationsDTO";
import QBDItemMappableFieldEnum from "@models/QuickBooks Desktop/QBDItemMappableFieldEnum";
import QBDItemSourceFieldEnum from "@models/QuickBooks Desktop/QBDItemSourceFieldEnum";
import { IQBDItemFieldMappings, QBD_ITEM_SOURCE_MAPPABLE_FIELDS } from "@models/QuickBooks Desktop/IQBDItemFieldMapping";

type Props = {
    closeSidebar: () => void;
};

type FieldRow = {
    field: QBDItemMappableFieldEnum;
    /** translation key for the Orderwerks field */
    label: string;
    /** translation key describing where the value comes from when no source is mapped */
    defaultSourceLabel: string;
};

/** Rows in the order they appear in the sidebar. */
const FIELD_ROWS: FieldRow[] = [
    { field: QBDItemMappableFieldEnum.name, label: "name", defaultSourceLabel: "qbd_item_field_default_name" },
    { field: QBDItemMappableFieldEnum.sku, label: "sku", defaultSourceLabel: "qbd_item_field_default_sku" },
    { field: QBDItemMappableFieldEnum.description, label: "description", defaultSourceLabel: "qbd_item_field_default_description" },
    { field: QBDItemMappableFieldEnum.upc, label: "upc", defaultSourceLabel: "qbd_item_field_default_upc" },
    { field: QBDItemMappableFieldEnum.msrp, label: "msrp", defaultSourceLabel: "qbd_item_field_default_msrp" },
    { field: QBDItemMappableFieldEnum.cost_per_uom, label: "cost_per_uom", defaultSourceLabel: "qbd_item_field_default_cost_per_uom" },
    {
        field: QBDItemMappableFieldEnum.unit_of_measurement_id,
        label: "unit_of_measurement",
        defaultSourceLabel: "qbd_item_field_default_unit_of_measurement",
    },
    { field: QBDItemMappableFieldEnum.is_taxable, label: "is_taxable", defaultSourceLabel: "qbd_item_field_default_is_taxable" },
    { field: QBDItemMappableFieldEnum.active, label: "active", defaultSourceLabel: "qbd_item_field_default_active" },
];

/** QBD values a text field can be mapped to (titles are translation keys). */
const SOURCE_OPTIONS: { value: QBDItemSourceFieldEnum; title: string }[] = [
    { value: QBDItemSourceFieldEnum.FullName, title: "qbd_source_full_name" },
    { value: QBDItemSourceFieldEnum.Name, title: "qbd_source_name" },
    { value: QBDItemSourceFieldEnum.ManufacturerPartNumber, title: "qbd_source_mfg_part_number" },
    { value: QBDItemSourceFieldEnum.SalesDesc, title: "qbd_source_sales_desc" },
    { value: QBDItemSourceFieldEnum.PurchaseDesc, title: "qbd_source_purchase_desc" },
    { value: QBDItemSourceFieldEnum.BarCodeValue, title: "qbd_source_barcode" },
];

/**
 * OR-1490: per Catalog Item field control of the QuickBooks Desktop item sync. For each Orderwerks field a
 * supplier can pick which QBD value feeds it (text fields) and switch off QBD updates to it entirely so edits
 * made in Orderwerks (e.g. marketing descriptions) survive full and incremental syncs. New items are always
 * populated from QBD; the switch only affects items that already exist.
 */
export const QbdItemFieldMappingsSidebar: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const intl = useIntl();

    const [mappings, setMappings] = useState<IQBDItemFieldMappings>({
        ...(supplierModel.Supplier_Integration.qbd_item_field_mappings || {}),
    });
    const [saving, setSaving] = useState(false);

    // SupplierOnUpdateSupplierIntegrations is shared by every integration-settings save, so only treat an
    // acknowledgment as ours while a save from this sidebar is outstanding (a ref, so the [] effect never
    // reads stale state).
    const pendingSave = useRef(false);

    useEffect(() => {
        socket.on(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onSaved);

        return () => {
            socket.off(EndpointsEnum.SupplierOnUpdateSupplierIntegrations, onSaved);
        };
    }, []);

    const onSaved = () => {
        if (!pendingSave.current) {
            return;
        }

        pendingSave.current = false;

        const notification: INotification = {
            message: "successfully_saved_item_field_mappings",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);

        setSaving(false);
        props.closeSidebar();
    };

    const save = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            Supplier_Integration: {
                qbd_item_field_mappings: mappings,
            } as ISupplier_Integration,
        };

        pendingSave.current = true;
        setSaving(true);
        socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);
    };

    const setSource = (field: QBDItemMappableFieldEnum, e: { target: { value: string } }) => {
        const value = e.target.value;

        setMappings({
            ...mappings,
            [field]: { ...(mappings[field] || {}), source: value ? (value as QBDItemSourceFieldEnum) : null },
        });
    };

    const setSkipUpdate = (field: QBDItemMappableFieldEnum, e: { target: { value: boolean } }) => {
        setMappings({
            ...mappings,
            [field]: { ...(mappings[field] || {}), skipUpdate: !!e.target.value },
        });
    };

    return (
        <QbdItemFieldMappingsSidebarWrapper className={"QbdItemFieldMappingsSidebar p-3"} data-testid={"qbd-item-field-mappings-sidebar"}>
            <div className={"d-flex align-items-center justify-content-between"}>
                <div>
                    <OwTitle title={"edit_item_field_mappings"} tag={"h4"} />
                </div>
                <div>
                    <OwButton className={"bg-danger"} centerIconClass={"fas fa-times"} fn={props.closeSidebar} />
                </div>
            </div>

            <div className={"mt-2 small"}>
                <OwLabel title={"qbd_item_field_mappings_info"} />
            </div>

            <div className="mt-4">
                <div className={"d-flex"}>
                    <div className={"w-25"}>
                        <OwLabel title={"orderwerks_field"} isBold={true} />
                    </div>
                    <div className={"w-50"}>
                        <OwLabel title={"qbd_source_field"} isBold={true} />
                    </div>
                    <div className={"w-25"}>
                        <OwLabel title={"do_not_update_from_qbd"} isBold={true} />
                    </div>
                </div>

                {FIELD_ROWS.map((row) => {
                    const mapping = mappings[row.field] || {};
                    const hasSource = QBD_ITEM_SOURCE_MAPPABLE_FIELDS.includes(row.field);

                    const sourceOptions = [{ value: "", title: row.defaultSourceLabel }, ...SOURCE_OPTIONS];

                    return (
                        <div className={"mt-3 d-flex align-items-center"} key={row.field} data-testid={`qbd-item-field-row-${row.field}`}>
                            <div className={"w-25"}>
                                <OwLabel title={row.label} />
                            </div>
                            <div className={"w-50 pe-3"}>
                                {hasSource ? (
                                    <OwSelectOption
                                        name={`source_${row.field}`}
                                        fn={(e: { target: { value: string } }) => setSource(row.field, e)}
                                        options={sourceOptions}
                                        titleProp={"title"}
                                        valueProp={"value"}
                                        translateTitle={true}
                                        showBlankFirst={false}
                                        selectedOption={{ source: mapping.source || "" }}
                                        selectedOptionProp={"source"}
                                        testId={`qbd-item-field-source-${row.field}`}
                                    />
                                ) : (
                                    <div className={"small text-muted"}>
                                        <OwLabel title={row.defaultSourceLabel} />
                                    </div>
                                )}
                            </div>
                            <div className={"w-25"}>
                                <OwToggle
                                    name={`skip_${row.field}`}
                                    label={""}
                                    disableTranslation={true}
                                    ariaLabel={`${intl.formatMessage({ id: row.label })}: ${intl.formatMessage({ id: "do_not_update_from_qbd" })}`}
                                    size={"small"}
                                    active={!!mapping.skipUpdate}
                                    fn={(e: { target: { value: boolean } }) => setSkipUpdate(row.field, e)}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className={"mt-4"}>
                <OwButton title={"save"} fn={save} active={saving} disableOnActive={true} testId={"qbd-item-field-mappings-save"} />
            </div>
        </QbdItemFieldMappingsSidebarWrapper>
    );
};

const QbdItemFieldMappingsSidebarWrapper = styled.div`
    max-height: 100vh;
    overflow-y: auto;
`;
